import { AgrDataSource } from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { isUserAllowedToOrderItem } from 'src/app/product/helpers/add-to-cart-helpers';
import { Environment } from 'src/environments/environment.model';

import { mapRepeatOrderItemToSkuCatalogItem, RepeatOrderItem, RepeatOrderItemVM } from '../../../models/repeat-order-item.model';

import { GetMultipleProductDeliveryDatesWith0FlexibilityResponse } from '../../../product/models/product-delivery-moment.models';
import { SkuCatalogItemViewData } from '../../../product/models/product.models';
import { selectOrderHistorySkuInformation, selectProposalDeliveryMomentsAndZeroFlex } from '../../../product/store/selectors';
import { selectRepeatOrder } from '../../../store/selectors';
import { calculateDeliveryInformation } from '../../../utils/deliveryInformation-helpers';

export abstract class AbstractRepeatOrderDataSource extends AgrDataSource<RepeatOrderItemVM> {
  private destroy$ = new Subject<void>();

  protected constructor(
    private readonly store: Store,
    private readonly hasAtLeastAdvisorRole$: Observable<boolean>,
    private readonly env: Environment,
  ) {
    super();
  }

  connect(): Observable<RepeatOrderItemVM[]> {
    return combineLatest([
      this.store.select(selectRepeatOrder),
      this.store.select(selectOrderHistorySkuInformation),
      this.store.select(selectProposalDeliveryMomentsAndZeroFlex),
      this.hasAtLeastAdvisorRole$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([source, skuCatalogItems, proposedDeliveryMoments, hasAtLeastAdvisorRole]) =>
        this.selectSource(source).map((item) => {
          let newItem = this.mapSkuCatalogItem(item, skuCatalogItems);
          newItem = this.mapProposalDeliveryMoments(newItem, proposedDeliveryMoments);
          return this.mapIsUserAllowedToOrderItem(newItem, hasAtLeastAdvisorRole);
        }),
      ),
      map((items) => items.sort((a, b) => this.sortOrderByDate(a, b))),
      tap((_) => this.loadingSubject.next(false)),
    );
  }

  disconnect(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  private mapSkuCatalogItem = (item: RepeatOrderItem, skusInformation: SkuCatalogItemViewData[]): RepeatOrderItem => ({
    ...item,
    orderingInformation: skusInformation.find((sku) => sku.skuId === item.internalId)?.orderingInformation,
  });

  private mapProposalDeliveryMoments = (
    item: RepeatOrderItem,
    proposalDeliveryMoments: GetMultipleProductDeliveryDatesWith0FlexibilityResponse,
  ): RepeatOrderItem => (item.deliveryInformation ? item : this.createInitialDeliveryInformation(item, proposalDeliveryMoments));

  private createInitialDeliveryInformation = (
    item: RepeatOrderItem,
    proposalDeliveryMoments: GetMultipleProductDeliveryDatesWith0FlexibilityResponse,
  ): RepeatOrderItem => ({
    ...item,
    deliveryInformation: calculateDeliveryInformation(proposalDeliveryMoments, item.internalId, item.orderingInformation),
  });

  private mapIsUserAllowedToOrderItem = (item: RepeatOrderItem, hasAtLeastAdvisorRole: boolean): RepeatOrderItemVM => ({
    ...item,
    isUserAllowedToOrderItem:
      this.env.ALLOWED_ORDER_ROLES_DISABLED || isUserAllowedToOrderItem(mapRepeatOrderItemToSkuCatalogItem(item), hasAtLeastAdvisorRole),
  });

  private sortOrderByDate(a: RepeatOrderItemVM, b: RepeatOrderItemVM): number {
    const aDate = new Date(a.latestOrder);
    const bDate = new Date(b.latestOrder);

    if (aDate === bDate) {
      return 0;
    }

    return aDate > bDate ? -1 : 1;
  }

  protected abstract selectSource(data: { bulk: RepeatOrderItem[]; generalCargo: RepeatOrderItem[] }): RepeatOrderItemVM[];
}
